import { useRef, useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Printer, X, Loader2, RefreshCw, ShieldCheck, Ban } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { openQrPrint, dealerPrintInfo } from "../../lib/qrPrint";
import { Button } from "../ui/button";

function fmtRelative(iso) {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "gerade aktiv";
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
    if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
    return `vor ${Math.floor(diff / 86400)} Tg`;
}

function withRel(list) {
    return (list || []).map((s) => ({ ...s, _rel: fmtRelative(s.last_seen) }));
}

/**
 * MobileInventoryQrModal — Jun-2026 (User-Wunsch).
 *
 * Live-QR enthält einen kurzlebigen, einmalig einlösbaren Geräte-Token
 * (`/auth/device-link`, 15 Min). Wird er am Handy gescannt, ist das Gerät
 * sofort eingeloggt (vollwertige Session) — kein erneutes Login nötig.
 * Der AUSDRUCK enthält bewusst nur den einfachen Link OHNE Token (ein
 * gedruckter Token würde ablaufen / wäre ein Sicherheitsrisiko).
 */
export default function MobileInventoryQrModal({ onClose, targetPath = "/dealer/fahrzeuginventur", title = "Inventur per Handy", subtitle = "Mit der Handy-Kamera scannen — das Gerät ist sofort eingeloggt und kann Inventurteile (FIN / EAN / QR) erfassen." }) {
    const { user } = useAuth();
    const qrRef = useRef(null);
    const plainUrl = `${window.location.origin}${targetPath}`;
    const sep = plainUrl.includes("?") ? "&" : "?";
    const [liveUrl, setLiveUrl] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [sessLoading, setSessLoading] = useState(true);
    const [revoking, setRevoking] = useState("");

    const loadSessions = async () => {
        setSessLoading(true);
        try {
            const { data } = await api.get("/auth/device-sessions");
            setSessions(withRel(data.sessions));
        } catch {
            setSessions([]);
        } finally {
            setSessLoading(false);
        }
    };

    const revokeSession = async (id) => {
        setRevoking(id);
        try {
            await api.post(`/auth/device-sessions/${id}/revoke`);
            setSessions((prev) => prev.filter((s) => s.id !== id));
            toast.success("Geräte-Login widerrufen");
        } catch {
            toast.error("Widerruf fehlgeschlagen");
        } finally {
            setRevoking("");
        }
    };

    const loadToken = async () => {
        setLoading(true);
        setError(false);
        try {
            const { data } = await api.post("/auth/device-link");
            setLiveUrl(`${plainUrl}${sep}devtoken=${encodeURIComponent(data.token)}`);
        } catch {
            setError(true);
            setLiveUrl(plainUrl); // Fallback: einfacher Link (Login am Handy nötig)
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const { data } = await api.post("/auth/device-link");
                if (active) setLiveUrl(`${plainUrl}${sep}devtoken=${encodeURIComponent(data.token)}`);
            } catch {
                if (active) { setError(true); setLiveUrl(plainUrl); }
            } finally {
                if (active) setLoading(false);
            }
        })();
        (async () => {
            try {
                const { data } = await api.get("/auth/device-sessions");
                if (active) setSessions(withRel(data.sessions));
            } catch {
                if (active) setSessions([]);
            } finally {
                if (active) setSessLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    const handlePrint = () => {
        // Druck nutzt bewusst den PLAIN-Link (kein Token) als eigenen QR.
        openQrPrint({
            title,
            subtitle: "Mit der Handy-Kamera scannen, um die Fahrzeug-Inventur-App zu öffnen. Anschließend am Handy einmalig anmelden.",
            scanUrl: plainUrl,
            ...dealerPrintInfo(user),
        });
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            data-testid="mobile-inventory-qr-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="w-full max-w-sm bg-white rounded-sm border-2 border-[#111827] p-6 text-center relative">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-400 hover:text-[#111827]"
                    aria-label="Schließen"
                    data-testid="mobile-inventory-qr-close"
                >
                    <X className="w-5 h-5" />
                </button>
                <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-2 py-1 rounded-sm mb-3">
                    <Smartphone className="w-3.5 h-3.5" /> {title}
                </div>
                <h2 className="font-display text-xl font-black mb-1.5">Handy-App starten</h2>
                <p className="text-sm text-gray-600 mb-4 leading-snug">
                    {subtitle}
                </p>

                <div ref={qrRef} className="inline-block border-2 border-[#111827] rounded-sm p-4 bg-white relative" data-testid="mobile-inventory-qr-code">
                    {loading ? (
                        <div className="w-[180px] h-[180px] flex items-center justify-center text-gray-400" data-testid="mobile-inventory-qr-loading">
                            <Loader2 className="w-8 h-8 animate-spin" />
                        </div>
                    ) : (
                        <QRCodeSVG value={liveUrl || plainUrl} size={180} level="M" />
                    )}
                    {/* Verstecktes Plain-QR ausschließlich für den Ausdruck (ohne Token). */}
                    <div id="print-qr" className="hidden">
                        <QRCodeSVG value={plainUrl} size={180} level="M" />
                    </div>
                </div>

                {error ? (
                    <p className="text-[11px] text-red-600 mt-3" data-testid="mobile-inventory-qr-error">
                        Geräte-Login nicht verfügbar — der QR öffnet die Seite, am Handy ist eine Anmeldung nötig.
                    </p>
                ) : (
                    <p className="text-[11px] text-gray-500 mt-3 flex items-center justify-center gap-1" data-testid="mobile-inventory-qr-hint">
                        <ShieldCheck className="w-3 h-3 text-green-600" /> Einmalig gültig · 15&nbsp;Min · sofort eingeloggt
                    </p>
                )}

                <div className="flex gap-2 mt-5">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={loadToken}
                        disabled={loading}
                        className="h-10 rounded-sm font-bold"
                        data-testid="mobile-inventory-qr-refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                    <Button
                        type="button"
                        onClick={handlePrint}
                        className="flex-1 h-10 rounded-sm bg-[#111827] hover:bg-black text-white font-bold"
                        data-testid="mobile-inventory-qr-print"
                    >
                        <Printer className="w-4 h-4 mr-1.5" /> QR-Code drucken
                    </Button>
                </div>

                {/* Aktive Geräte-Logins (einzeln widerrufbar, z. B. verlorenes Handy). */}
                <div className="mt-5 pt-4 border-t border-gray-200 text-left" data-testid="device-sessions-block">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1">
                            <Smartphone className="w-3.5 h-3.5" /> Aktive Geräte-Logins
                        </span>
                        <button
                            type="button"
                            onClick={loadSessions}
                            className="text-gray-400 hover:text-[#111827]"
                            aria-label="Aktualisieren"
                            data-testid="device-sessions-refresh"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${sessLoading ? "animate-spin" : ""}`} />
                        </button>
                    </div>
                    {sessLoading ? (
                        <div className="text-xs text-gray-400 py-2 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt…</div>
                    ) : sessions.length === 0 ? (
                        <p className="text-xs text-gray-400 py-1" data-testid="device-sessions-empty">Keine aktiven Geräte-Logins.</p>
                    ) : (
                        <ul className="space-y-1.5" data-testid="device-sessions-list">
                            {sessions.map((s) => (
                                <li key={s.id} className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-sm px-2.5 py-1.5" data-testid={`device-session-${s.id}`}>
                                    <div className="min-w-0">
                                        <div className="text-xs font-bold text-[#111827] truncate">{s.device_label}</div>
                                        <div className="text-[10px] text-gray-500">aktiv {s._rel}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => revokeSession(s.id)}
                                        disabled={revoking === s.id}
                                        className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-red-600 hover:text-red-700 border border-red-200 hover:border-red-400 rounded-sm px-2 py-1 disabled:opacity-50"
                                        data-testid={`device-session-revoke-${s.id}`}
                                    >
                                        {revoking === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />} Widerrufen
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
